import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [extensionConnected, setExtensionConnected] = useState(false);
  const connectionIntervalRef = useRef(null);

  useEffect(() => {
    const verifyUser = () => {
      console.log("AuthContext: Verifying session from local storage...");
      setIsLoading(true);

      const storedUserData = localStorage.getItem("userData");

      if (storedUserData) {
        try {
          const parsedUser = JSON.parse(storedUserData);
          console.log(
            "AuthContext: Found user data. Restoring session for:",
            parsedUser.email
          );
          setUser(parsedUser);
          setIsAuthenticated(true);
        } catch (e) {
          console.error("AuthContext: Failed to parse stored user data.", e);
          localStorage.removeItem("userData");
          setUser(null);
          setIsAuthenticated(false);
        }
      } else {
        console.log("AuthContext: No user data found. Session not active.");
        setUser(null);
        setIsAuthenticated(false);
      }

      setIsLoading(false);
    };

    verifyUser();
  }, []);

  useEffect(() => {
    const handleExtensionMessage = (event) => {
      if (event.source !== window || event.origin !== window.location.origin)
        return;
      if (event.data.type === "EXTENSION_READY") {
        setExtensionConnected(true);
        if (connectionIntervalRef.current) {
          console.log(
            "AuthContext: Extension connection confirmed. Stopping pings."
          );
          clearInterval(connectionIntervalRef.current);
          connectionIntervalRef.current = null;
        }
      }
    };
    window.addEventListener("message", handleExtensionMessage);

    if (!extensionConnected) {
      console.log("AuthContext: Starting to ping for extension connection...");
      connectionIntervalRef.current = setInterval(() => {
        window.postMessage({ type: "DASHBOARD_READY" }, window.location.origin);
      }, 1000);
    }

    return () => {
      window.removeEventListener("message", handleExtensionMessage);
      if (connectionIntervalRef.current) {
        clearInterval(connectionIntervalRef.current);
      }
    };
  }, [extensionConnected]);

  const login = useCallback((loggedInUserData) => {
    localStorage.setItem("userData", JSON.stringify(loggedInUserData));
    setUser(loggedInUserData);
    setIsAuthenticated(true);
    setIsLoading(false);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("userData");
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      extensionConnected,
      login,
      logout,
    }),
    [user, isAuthenticated, isLoading, extensionConnected, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
